import * as React from 'react';
import { cn } from '../utils';
import { LineDisplay } from './LineDisplay';
import { getLiquidityTooltip, type LiquidityThresholds } from './liquidity';
import { Tooltip } from '../primitives/Tooltip';
import type { EVVariant } from '../utils';
import type { BestLines, BestLine } from '@sharpr/types';
import { useBook } from './bookCatalog';
import { ChangePulse, linePulseValue } from './ChangePulse';

export interface BestLineSummaryProps {
  bestLines: BestLines;
  className?: string;
  compact?: boolean;
  evVariant?: EVVariant;
  liquidityThresholds?: LiquidityThresholds;
  /** Render a blurred ghost in the EV slot when EV is absent (non-subscriber teaser). */
  teaseEv?: boolean;
  /** Render a construction glyph in the EV slot for no-model markets (e.g. MLB batters). */
  teaseConstruction?: boolean;
}

/**
 * Mirrors LineSlot in SportsbookLineCell so both columns share the exact same
 * grid-row → h-full-slot → LineDisplay chain. Diverging on wrapper sizing (e.g.
 * `min-h-0` without `h-full`) causes the inner LineDisplay to shrink to content
 * height and anchor to the top of its grid row instead of centering — the
 * "content shifted up" bug.
 */
const BestSlot: React.FC<{
  line: BestLine | undefined;
  side: 'over' | 'under';
  evVariant: EVVariant;
  liquidityThresholds?: LiquidityThresholds;
  teaseEv?: boolean;
  teaseConstruction?: boolean;
}> = ({ line, side, evVariant, liquidityThresholds, teaseEv, teaseConstruction }) => {
  const slotClass = cn(
    "relative isolate block w-full h-full",
    side === 'over' && "border-b border-border/30"
  );

  return (
    <div className={slotClass}>
      <ChangePulse value={linePulseValue(line)} />
      {line && (
        <FilledSlot line={line} evVariant={evVariant} liquidityThresholds={liquidityThresholds} teaseEv={teaseEv} teaseConstruction={teaseConstruction} />
      )}
    </div>
  );
};

const FilledSlot: React.FC<{
  line: BestLine;
  evVariant: EVVariant;
  liquidityThresholds?: LiquidityThresholds;
  teaseEv?: boolean;
  teaseConstruction?: boolean;
}> = ({ line, evVariant, liquidityThresholds, teaseEv, teaseConstruction }) => {
  const { name, book } = useBook(line.book);
  const bookInfo = { name, limitKind: book?.limitKind ?? null };

  const tooltip = getLiquidityTooltip(line, bookInfo);
  const content = (
    <LineDisplay
      line={line}
      showThumbnail={true}
      size="sm"
      evVariant={evVariant}
      liquidityThresholds={liquidityThresholds}
      teaseEv={teaseEv}
      teaseConstruction={teaseConstruction}
    />
  );

  return tooltip ? (
    <Tooltip
      content={tooltip}
      side="top"
      wrapperClassName="block w-full h-full"
      delayMs={120}
    >
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export const BestLineSummary: React.FC<BestLineSummaryProps> = ({
  bestLines,
  className,
  compact = false,
  evVariant = 'default',
  liquidityThresholds,
  teaseEv = false,
  teaseConstruction = false,
}) => {
  if (!bestLines.over && !bestLines.under) return null;

  return (
    <div className={cn(
      "h-full w-full grid grid-cols-1 grid-rows-2",
      compact && "text-xs",
      className
    )}>
      <BestSlot line={bestLines.over} side="over" evVariant={evVariant} liquidityThresholds={liquidityThresholds} teaseEv={teaseEv} teaseConstruction={teaseConstruction} />
      <BestSlot line={bestLines.under} side="under" evVariant={evVariant} liquidityThresholds={liquidityThresholds} teaseEv={teaseEv} teaseConstruction={teaseConstruction} />
    </div>
  );
};
